import { FC, useContext } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled from 'styled-components';

import ClickableButton from '@/components/Buttons/ClickableButton';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

const StartWithViberChatbot: FC<any> = ({ data, handleClick }) => {
	const { lang } = useContext(GlobalContext);

	return (
		<Wrapper>
			<MobTitleGrad src="/images/chatbots/StartMobTitleGrad.png" />

			<MobBottomGrad src="/images/chatbots/StartBotMobGrad.png" />

			<TitleGrad src="/images/chatbots/StartTitleGrad.png" />

			<RightGrad src="/images/chatbots/StartRightGrad.png" />

			<ScrollAnimation animateOnce animateIn="fadeInUp">
				<Title>{data?.title ? parseHTML(data?.title) : null}</Title>

				<Description>
					{data?.description ? parseHTML(data?.description) : null}
				</Description>
			</ScrollAnimation>

			<Content>
				{data?.steps?.map((step: any, index: number) => (
					<ContentTile key={`cb_step_${index}`}>
						<ScrollAnimation
							animateOnce
							animateIn="fadeInUp"
							delay={(index + 1) * 200}
						>
							<SectionTitle>{parseHTML(step?.title)}</SectionTitle>

							{step?.description ? (
								<SectionText>{parseHTML(step?.description)}</SectionText>
							) : null}
						</ScrollAnimation>
					</ContentTile>
				))}
			</Content>

			<ButtonWrapper>
				<ScrollAnimation
					animateOnce
					offset={0}
					delay={400}
					animateIn="fadeInUp"
				>
					<ClickableButton
						text={translate(lang, {
							en: 'Contact Us',
							ua: 'Звʼязатися з нами',
							ru: 'Связаться с нами',
						})}
						onClick={handleClick}
					/>
				</ScrollAnimation>

				<ScrollAnimation
					animateOnce
					animateIn="fadeInUp"
					offset={0}
					delay={600}
				>
					<PrimaryButton
						width="wide"
						text={data?.cta?.text}
						href={data?.cta?.link}
					/>
				</ScrollAnimation>
			</ButtonWrapper>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 116px 15px 0;
	width: 990px;
	max-width: 100%;
	margin: 0 auto;

	@media (max-width: 800px) {
		padding: 25px 15px;
	}

	@media (max-width: 500px) {
		padding: 50px 15px;
	}
`;

const MobTitleGrad = styled.img`
	display: none;
	z-index: -100;

	@media (max-width: 375px) {
		display: block;
		position: absolute;
		top: 27%;
		right: 0;
	}
`;

const MobBottomGrad = styled.img`
	display: none;
	z-index: -100;

	@media (max-width: 375px) {
		display: block;
		position: absolute;
		right: 0;
		top: 59%;
	}
`;

const TitleGrad = styled.img`
	position: absolute;
	right: 16%;
	top: 14%;
	z-index: -100;

	@media (max-width: 1700px) {
		right: 11%;
	}

	@media (max-width: 1400px) {
		top: 14%;
		right: 0;
	}

	@media (max-width: 950px) {
		top: 27%;
	}

	@media (max-width: 650px) {
		top: 31%;
	}

	@media (max-width: 800px) {
		width: 100%;
	}

	@media (max-width: 375px) {
		display: none;
	}
`;

const RightGrad = styled.img`
	position: absolute;
	right: 0;
	top: 43%;
	z-index: -100;

	@media (max-width: 1400px) {
		top: 50%;
	}

	@media (max-width: 950px) {
		top: 54%;
	}

	@media (max-width: 800px) {
		width: 100%;
	}

	@media (max-width: 375px) {
		display: none;
	}
`;

const Title = styled.h2`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);
	text-align: center;
	margin-bottom: 20px;

	@media (max-width: 500px) {
		width: 80%;
		margin: 0 auto 15px;
		font-size: 28px;
		line-height: 32px;
	}
`;

const Description = styled.div`
	font-family: var(--font-sans);
	color: var(--color-black);
	font-size: 18px;
	line-height: 25px;
	margin: 0 auto 60px;
	width: 720px;
	max-width: 100%;
	text-align: center;

	@media (max-width: 800px) {
		font-size: 16px;
		margin-bottom: 40px;
	}
`;

const Content = styled.div`
	display: flex;
	justify-content: center;
	column-gap: 70px;

	@media (max-width: 875px) {
		flex-direction: column;
		align-items: center;
	}
`;

const ContentTile = styled.div`
	width: 276px;
	max-width: 32%;
	position: relative;

	&:before {
		content: '';
		width: 21px;
		height: 19px;
		left: -50px;
		top: 62px;
		display: block;
		position: absolute;
		background: url(/images/messaging-solutions/partner-arrow.svg);
	}

	&:first-child {
		&:before {
			display: none;
		}
	}

	@media (max-width: 875px) {
		width: 370px;
		max-width: 100%;
		margin-bottom: 80px;

		&:last-child {
			margin-bottom: 0;

			&:before {
				display: none;
			}
		}

		&:first-child {
			&:before {
				display: block;
			}
		}

		&:before {
			top: calc(100% + 30px);
			left: 50%;
			margin-left: -10px;
			transform: rotate(90deg);
		}
	}
`;

const SectionTitle = styled.h3`
	width: 300px;
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 16px;
	line-height: 24px;
	color: var(--color-purple);
	margin-bottom: 39px;
	text-transform: uppercase;

	@media (max-width: 800px) {
		font-size: 14px;
		line-height: 21px;
		margin-bottom: 10px;
	}
`;

const SectionText = styled.div`
	font-family: var(--font-sans);
	font-size: 16px;
	line-height: 24px;
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: center;
	column-gap: 60px;
	margin-top: 60px;

	@media (max-width: 500px) {
		flex-direction: column;
		row-gap: 20px;
		margin-top: 40px;

		button {
			width: 100%;
			margin: 0 auto;
		}
	}
`;

export default StartWithViberChatbot;
